import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import * as Sentry from "@sentry/react";

import { LINK } from "@lexical/markdown";
import { LinkNode } from "@lexical/link";
import { CreateEditorArgs, EditorState } from "lexical";
import React, { FocusEventHandler, useCallback } from "react";
import { ReadOnlyPlugin } from "./DisablePlugin";
import MentionPlugin from "./MentionPlugin";
import { MentionNode } from "./MentionPlugin/MentionNode";
import { IHumanReadableCoding } from "@/data-models/value-models/types";
import { $convertToMarkdown, $convertFromMarkdown } from "./convertors";
import FloatingMentionTooltipPlugin from "./FloatingMentionTooltipPlugin";
import { DefinedValueSet } from "@/services/content/content-client";
import TreeViewPlugin from "./TreeViewPlugin";
import AutoFocusPlugin from "./AutoFocusPlugin";
export type MarkdownText = string;

export type MarkdownTextEditorProps<TEntity extends {}> = {
  disabled?: boolean;
  readOnly?: boolean;
  border?: true | "hide";
  active?: boolean;
  autoFocus?: boolean;
  className?: string;
  value?: MarkdownText;
  children?: React.ReactNode;
  placeholder?: string | null;
  valueSet?: string | DefinedValueSet;
  initialEntityMap?: Map<string, TEntity> | Record<string, TEntity>;
  debug?: boolean;
  onAddEntity?: (id: string, entity: TEntity) => void;
  onRemoveEntity?: (id: string) => void;
  renderEntity?: (id: string) => JSX.Element | null;
  onChange?: (value: MarkdownText) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  onError?: (error: Error) => void;
};

export const EDITOR_CONFIG: CreateEditorArgs = {
  nodes: [LinkNode, MentionNode],
};
type Entity = IHumanReadableCoding;
function MarkdownTextEditor({
  disabled = false,
  readOnly = false,
  border = true,
  active,
  value = "",
  placeholder = null,
  valueSet = "http://tiro.health/fhir/ValueSet/snomed-ct-auto-generated",
  debug = false,
  autoFocus = false,
  onAddEntity,
  onRemoveEntity,
  renderEntity,
  initialEntityMap,
  className,
  onChange,
  onBlur,
  onError,
}: MarkdownTextEditorProps<Entity>) {
  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const markdown = $convertToMarkdown();
        onChange && onChange(markdown);
      });
    },
    [onChange],
  );

  return (
    <LexicalComposer
      initialConfig={{
        namespace: "markdown",
        onError(error) {
          Sentry.captureException(error);
          onError?.(error);
        },
        editable: !disabled,
        editorState: () => $convertFromMarkdown(value),
        ...EDITOR_CONFIG,
      }}
    >
      <>
        <div className={className}>
          <AutoFocusPlugin autoFocus={autoFocus} />
          <MarkdownShortcutPlugin transformers={[LINK]} />
          <MentionPlugin
            valueSet={valueSet}
            onAddEntity={onAddEntity}
            onRemoveEntity={onRemoveEntity}
            initialEntityMap={initialEntityMap}
          />
          <FloatingMentionTooltipPlugin renderEntity={renderEntity} />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                onBlur={onBlur}
                className="relative grow bg-transparent focus:outline-none"
                data-testid="markdown-editor"
                disabled={disabled}
              />
            }
            placeholder={
              <div
                className="pointer-events-none absolute inset-0 select-none px-2 py-1 text-gray-light"
                data-testid="placeholder"
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ReadOnlyPlugin readOnly={disabled || readOnly} />
          <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
        </div>
        {debug && <TreeViewPlugin />}
      </>
    </LexicalComposer>
  );
}
export default MarkdownTextEditor;
